import { db } from "./db";
import type { Backend, PendingMutation, Project } from "./types";

const CLOUD_URL = (import.meta.env.VITE_CLOUD_API_URL || "").replace(/\/$/, "");

export class ApiError extends Error {
  constructor(public status: number, public payload: Record<string, unknown>) {
    super(String(payload.detail || `HTTP ${status}`));
  }
}

function headers(backend: Backend, json = true): HeadersInit {
  const result: Record<string, string> = {};
  if (json) result["Content-Type"] = "application/json";
  if (backend.token) result.Authorization = `Bearer ${backend.token}`;
  return result;
}

export async function request<T>(backend: Backend, path: string, init: RequestInit = {}): Promise<T> {
  const response = await fetch(`${backend.baseUrl}${path}`, {
    ...init,
    headers: { ...headers(backend, init.body !== undefined && !(init.body instanceof Blob)), ...(init.headers || {}) },
    credentials: backend.kind === "cloud" ? "include" : "omit",
  });
  const payload = response.headers.get("content-type")?.includes("json") ? await response.json() : {};
  if (!response.ok) throw new ApiError(response.status, payload);
  return payload as T;
}

async function health(baseUrl: string, timeout = 1600): Promise<boolean> {
  const controller = new AbortController();
  const timer = window.setTimeout(() => controller.abort(), timeout);
  try {
    const response = await fetch(`${baseUrl}/api/v1/health`, { signal: controller.signal, credentials: "include" });
    return response.ok;
  } catch {
    return false;
  } finally {
    clearTimeout(timer);
  }
}

export async function discoverBackends(): Promise<{ mac: Backend; cloud: Backend }> {
  const macUrl = (localStorage.getItem("macUrl") || "").replace(/\/$/, "");
  const macToken = localStorage.getItem("macToken") || undefined;
  const [macAvailable, cloudAvailable] = await Promise.all([
    macUrl ? health(macUrl) : Promise.resolve(false),
    health(CLOUD_URL),
  ]);
  return {
    mac: { kind: "mac", baseUrl: macUrl, token: macToken, available: macAvailable, label: "Mac" },
    cloud: { kind: "cloud", baseUrl: CLOUD_URL, available: cloudAvailable, label: "Cloud" },
  };
}

export async function pairMac(macUrl: string, pairId: string, code: string): Promise<void> {
  const backend: Backend = { kind: "mac", baseUrl: macUrl.replace(/\/$/, ""), available: true, label: "Mac" };
  const paired = await request<{ token: string }>(backend, "/api/v1/pair/exchange", {
    method: "POST",
    body: JSON.stringify({ pair_id: pairId, code, device_name: navigator.userAgent.includes("iPhone") ? "Bao iPhone" : "Bao zařízení" }),
  });
  localStorage.setItem("macUrl", backend.baseUrl);
  localStorage.setItem("macToken", paired.token);
}

export async function getProject(backend: Backend, id: string): Promise<Project> {
  return request(backend, `/api/v1/projects/${id}`);
}

export async function artifactUrl(backend: Backend, id: string, kind: string): Promise<string> {
  const result = await request<{ url: string }>(backend, `/api/v1/projects/${id}/artifacts/${kind}`);
  return result.url;
}

export async function mutate<T>(backend: Backend, path: string, method: string, body: unknown, queueOffline = true): Promise<T | null> {
  try {
    return await request<T>(backend, path, { method, body: JSON.stringify(body) });
  } catch (error) {
    if (queueOffline && (!(error instanceof ApiError) || error.status >= 500)) {
      await db.putLatestMutation({ backend: backend.kind, baseUrl: backend.baseUrl, token: backend.token, path, method, body, createdAt: Date.now() });
      return null;
    }
    throw error;
  }
}

export async function flushMutations(): Promise<void> {
  const pending = await db.allMutations();
  const revisions = new Map<string, number>();
  for (const mutation of pending) {
    const backend: Backend = { kind: mutation.backend, baseUrl: mutation.baseUrl, token: mutation.token, available: true, label: mutation.backend };
    try {
      const match = mutation.path.match(/\/projects\/([^/]+)/);
      const projectId = match?.[1];
      const body = { ...(mutation.body as Record<string, unknown>) };
      if (projectId && revisions.has(projectId) && typeof body.revision === "number") body.revision = revisions.get(projectId)!;
      const result = await request<Record<string, unknown>>(backend, mutation.path, { method: mutation.method, body: JSON.stringify(body) });
      if (projectId && typeof result.revision === "number") revisions.set(projectId, result.revision);
      if (mutation.id) await db.deleteMutation(mutation.id);
    } catch (error) {
      if (error instanceof ApiError && error.status === 409) {
        if (mutation.id) await db.deleteMutation(mutation.id);
        window.dispatchEvent(new CustomEvent("revision-conflict"));
      }
      break;
    }
  }
}

export function cloudBackend(): Backend {
  return { kind: "cloud", baseUrl: CLOUD_URL, available: true, label: "Cloud" };
}
